import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SA_API = `${API_URL}/api/superadmin`;

const SuperAdminContext = createContext(null);

export function SuperAdminProvider({ children }) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const r = await fetch(`${SA_API}/me`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setEmail(data.email);
      } else {
        setEmail(null);
      }
    } catch {
      setEmail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (emailVal, password) => {
    const r = await fetch(`${SA_API}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailVal, password }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await r.json();
    setEmail(data.user?.email || emailVal);
    return data;
  };

  const logout = async () => {
    try { await fetch(`${SA_API}/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    setEmail(null);
  };

  const apiFetch = useCallback(async (path, options = {}) => {
    const url = path.startsWith('http') ? path : `${SA_API}${path}`;
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, []);

  return (
    <SuperAdminContext.Provider value={{
      email,
      isAuthenticated: !!email,
      loading,
      login,
      logout,
      apiFetch,
      token: email, // retained for legacy page imports; cookie-based auth used in practice
    }}>
      {children}
    </SuperAdminContext.Provider>
  );
}

export function useSuperAdmin() {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  return ctx;
}
